import React, { useEffect, useRef } from 'react';
import { Check, X, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

export const PLANS = [
  { key: 'explorer',   name: 'Explorer',   price: 49,  tagline: 'Begin your brain-training journey' },
  { key: 'challenger', name: 'Challenger', price: 99,  tagline: 'For everyday brain athletes', highlight: true },
  { key: 'master',     name: 'Master',     price: 149, tagline: 'Unlock every advantage' },
];

// [feature, free, explorer, challenger, master]
const ROWS = [
  ['7-day free trial (no charge for 7 days)', false, true, true, true],
  ['Daily puzzles', '5 / day', 'Unlimited', 'Unlimited', 'Unlimited'],
  ['Logic, Memory, Speed, Attention & Math', true, true, true, true],
  ['Mixed challenges (all skills in one session)', false, false, true, true],
  ['Speaking, writing & English challenges', true, true, true, true],
  ['Brain Score, XP & streak tracker', true, true, true, true],
  ['Leaderboards (weekly, monthly, all-time)', true, true, true, true],
  ['Detailed progress tracking & trends', false, false, true, true],
  ['Priority customer support', false, false, true, true],
  ['Advanced insights & personalised weekly reports', false, false, false, true],
  ['Premium exclusive challenges', false, false, false, true],
  ['Concierge support', false, false, false, true],
];

const Mark = ({ on, plan }) => (
  typeof on === 'string'
    ? <span className="text-xs sm:text-sm font-semibold text-[#F5EDE0]">{on}</span>
    : on
    ? <span className="inline-grid place-items-center h-7 w-7 rounded-full bg-[#F4C430]/15 text-[#F4C430]" aria-label={`Included in ${plan}`}><Check size={16} strokeWidth={3} /></span>
    : <span className="inline-grid place-items-center h-7 w-7 rounded-full bg-[#2A1810] text-[#8f7658]" aria-label={`Not included in ${plan}`}><X size={15} strokeWidth={2.5} /></span>
);

// Plans as columns, features as rows. Challenger column is the featured/recommended choice.
const PlanComparison = ({ onChoose, currentPlan, busyPlan, showFree = false }) => {
  const scroller = useRef(null);
  // On narrow screens, bring the recommended Challenger column into view first
  useEffect(() => {
    const align = () => {
      const el = scroller.current;
      if (showFree || !el || el.scrollWidth <= el.clientWidth || el.dataset.aligned) return;
      const target = el.querySelector('[data-testid="plan-col-challenger"]');
      const sticky = el.querySelector('thead th');
      if (!target || !sticky) return;
      el.scrollLeft = Math.max(0, target.offsetLeft - sticky.offsetWidth - 8);
      el.dataset.aligned = '1';
    };
    const t = setTimeout(align, 350);
    window.addEventListener('resize', align);
    return () => { clearTimeout(t); window.removeEventListener('resize', align); };
  }, [showFree]);
  const col = (p) => (p.highlight ? 'bg-[#F4C430]/[0.07] border-x-2 border-[#F4C430]' : '');
  return (
    <div ref={scroller} className="relative rounded-2xl border border-[#4a3221] bg-[#2A1810]/70 overflow-x-auto scroll-smooth" data-testid="plan-comparison">
      <table className="w-full min-w-[720px] sm:min-w-[640px] border-collapse text-sm">
        <caption className="sr-only">Compare The Brain Theory plans</caption>
        <thead>
          <tr>
            <th scope="col" className="sticky left-0 z-10 bg-[#2A1810] text-left align-bottom p-3 sm:p-5 w-[150px] sm:w-[38%] shadow-[6px_0_10px_-8px_rgba(0,0,0,0.6)] sm:shadow-none">
              <span className="text-[#B8A990] text-xs uppercase tracking-widest">Features</span>
            </th>
            {showFree && (
              <th scope="col" className="p-4 sm:p-5 pt-11 sm:pt-11 text-center align-top min-w-[110px]" data-testid="plan-col-free">
                <div className="font-serif text-lg text-[#D9C9B3]">Free</div>
                <div className="mt-1 text-2xl font-serif text-[#D9C9B3]">₹0</div>
                <div className="text-[11px] text-[#8f7658]">forever</div>
              </th>
            )}
            {PLANS.map((p) => (
              <th key={p.key} scope="col" className={`relative p-4 sm:p-5 pt-11 sm:pt-11 text-center align-top ${col(p)} ${p.highlight ? 'border-t-2 rounded-t-xl' : ''}`} data-testid={`plan-col-${p.key}`}>
                {p.highlight && (
                  <span className="absolute top-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-[#F4C430] text-[#2A1810] text-[10px] font-bold px-2.5 py-0.5 whitespace-nowrap" data-testid="plan-badge-popular">
                    <Sparkles size={10} /> Most Popular · Best Value
                  </span>
                )}
                <div className={`font-serif text-xl sm:text-2xl ${p.highlight ? 'text-[#F4C430]' : 'text-[#F5EDE0]'}`}>{p.name}</div>
                <div className="mt-1 text-[11px] text-[#B8A990] font-normal leading-snug">{p.tagline}</div>
                <div className="mt-3 flex items-end justify-center gap-1">
                  <span className={`font-serif ${p.highlight ? 'text-4xl sm:text-5xl' : 'text-3xl sm:text-4xl'} text-[#F4C430]`}>₹{p.price}</span>
                  <span className="text-[#B8A990] text-xs pb-1.5 font-normal">/month</span>
                </div>
                <div className="mt-1 text-[11px] text-[#D9C9B3] font-normal">
                  {p.highlight ? <>Just ₹{(p.price / 30).toFixed(1)}/day · </> : null}7 days free, then billed monthly
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {ROWS.map(([label, ...vals], i) => (
            <tr key={label} className="border-t border-[#4a3221]">
              <th scope="row" className="sticky left-0 z-10 bg-[#2A1810] text-left font-normal text-[#E8DCC8] text-xs sm:text-sm p-3 sm:px-5 shadow-[6px_0_10px_-8px_rgba(0,0,0,0.6)] sm:shadow-none">{label}</th>
              {showFree && (
                <td className="p-3 text-center" data-testid={`plan-cell-free-${i}`}><Mark on={vals[0]} plan="Free" /></td>
              )}
              {PLANS.map((p, j) => (
                <td key={p.key} className={`p-3 text-center ${col(p)}`} data-testid={`plan-cell-${p.key}-${i}`}>
                  <Mark on={vals[j + 1]} plan={p.name} />
                </td>
              ))}
            </tr>
          ))}
          <tr className="border-t border-[#4a3221]">
            <td className="sticky left-0 z-10 bg-[#2A1810] p-4 sm:px-5 text-[11px] text-[#8f7658]">Auto-renews monthly · Cancel anytime from Profile → Subscription</td>
            {showFree && <td className="p-4 text-center text-[11px] text-[#8f7658]">No payment details</td>}
            {PLANS.map((p) => (
              <td key={p.key} className={`p-4 text-center ${col(p)} ${p.highlight ? 'border-b-2 rounded-b-xl' : ''}`}>
                <Button
                  onClick={() => onChoose(p.key)}
                  disabled={currentPlan === p.key || busyPlan === p.key}
                  data-testid={`plan-choose-${p.key}`}
                  className={`w-full rounded-full h-11 font-semibold ${p.highlight
                    ? 'bg-[#F4C430] hover:bg-[#FFD34E] text-[#2A1810] shadow-[0_12px_30px_-12px_rgba(244,196,48,0.7)]'
                    : 'bg-transparent text-[#F4C430] border border-[#F4C430]/60 hover:bg-[#F4C430] hover:text-[#2A1810]'}`}
                >
                  {currentPlan === p.key ? 'Current plan' : `Choose ${p.name}`}
                </Button>
              </td>
            ))}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default PlanComparison;
